import { useState, useEffect, useMemo } from 'react';
import { ROLES, ROLES_CAP, COMITE_LEVEL_ROLES } from '../utils/Constants';
import { getRolesFromToken, hasRole, hasAnyRole } from '../utils/Utils';
import { hasCommitteeOnlyAccess } from '../utils/uruguayRoles';

const useRole = () => {
  const role = localStorage.getItem('roles');
  const [currentRole, setCurrentRole] = useState<string>('');

  useEffect(() => {
    if (role) {
      if (hasRole(ROLES_CAP.admin)) {
        setCurrentRole(ROLES.admin);
      } else if (hasRole(ROLES_CAP.producer)) {
        setCurrentRole(ROLES.producer);
      } else if (hasRole(ROLES_CAP.commercial)) {
        setCurrentRole(ROLES.commercial);
      } else if (hasRole(ROLES_CAP.riskAnalyst)) {
        setCurrentRole(ROLES.riskAnalyst);
      } else if (hasRole(ROLES_CAP.instrumentator)) {
        setCurrentRole(ROLES.instrumentator);
      } else if (hasAnyRole(COMITE_LEVEL_ROLES)) {
        setCurrentRole(ROLES.comite);
      }
    }
  }, [role]);

  const isAdmin = currentRole === ROLES.admin;
  const isProducer = currentRole === ROLES.producer;
  const isCommercial = currentRole === ROLES.commercial;
  const isRiskAnalyst = currentRole === ROLES.riskAnalyst;
  const isComite = currentRole === ROLES.comite;
  const isInstrumentator = currentRole === ROLES.instrumentator;

  const isCommitteeOnly = useMemo(
    () => hasCommitteeOnlyAccess(getRolesFromToken()),
    [role],
  );

  const isPureRiskAnalyst = useMemo(() => {
    if (!hasRole(ROLES_CAP.riskAnalyst) || hasRole(ROLES_CAP.admin)) {
      return false;
    }
    const hasFlowRole =
      hasRole(ROLES_CAP.producer) ||
      hasRole(ROLES_CAP.commercial) ||
      hasAnyRole(COMITE_LEVEL_ROLES) ||
      hasRole(ROLES_CAP.instrumentator);
    return !hasFlowRole;
  }, [role]);

  const canMutateData = useMemo(
    () =>
      isAdmin ||
      isCommercial ||
      isRiskAnalyst ||
      isComite ||
      isInstrumentator ||
      isProducer,
    [isAdmin, isCommercial, isRiskAnalyst, isComite, isInstrumentator, isProducer],
  );

  const canTransitionOrigination = useMemo(() => {
    if (hasRole(ROLES_CAP.admin)) return true;
    if (hasRole(ROLES_CAP.producer)) return true;
    if (hasRole(ROLES_CAP.commercial)) return true;
    if (hasAnyRole(COMITE_LEVEL_ROLES)) return true;
    if (hasRole(ROLES_CAP.instrumentator)) return true;
    if (isPureRiskAnalyst) return true;
    return false;
  }, [role, isPureRiskAnalyst]);

  const canTransitionRisk = useMemo(() => {
    if (hasRole(ROLES_CAP.admin)) return true;
    if (hasRole(ROLES_CAP.producer)) return true;
    if (hasRole(ROLES_CAP.commercial)) return true;
    if (hasAnyRole(COMITE_LEVEL_ROLES)) return true;
    if (hasRole(ROLES_CAP.instrumentator)) return true;
    if (isPureRiskAnalyst) return true;
    return false;
  }, [role, isPureRiskAnalyst]);

  const canTransitionFlow = useMemo(
    () => canTransitionOrigination || canTransitionRisk,
    [canTransitionOrigination, canTransitionRisk],
  );

  const canDeclineInCommittee = useMemo(
    () => isAdmin || isRiskAnalyst || isComite || isCommercial || isInstrumentator,
    [isAdmin, isRiskAnalyst, isComite, isCommercial, isInstrumentator],
  );

  const canApproveInCommittee = useMemo(() => {
    if (isAdmin) return true;
    if (isRiskAnalyst) return false;
    return isComite || isCommercial || isInstrumentator;
  }, [isAdmin, isRiskAnalyst, isComite, isCommercial, isInstrumentator]);

  const canViewInstrumentation = useMemo(
    () =>
      !isProducer &&
      (isAdmin ||
        isCommercial ||
        isRiskAnalyst ||
        isComite ||
        isInstrumentator),
    [isProducer, isAdmin, isCommercial, isRiskAnalyst, isComite, isInstrumentator],
  );

  const canEditInstrumentation = useMemo(
    () => isAdmin || isInstrumentator,
    [isAdmin, isInstrumentator],
  );

  return {
    isAdmin,
    isProducer,
    isCommercial,
    isRiskAnalyst,
    isPureRiskAnalyst,
    isComite,
    isCommitteeOnly,
    isInstrumentator,
    canMutateData,
    canTransitionOrigination,
    canTransitionRisk,
    canTransitionFlow,
    canDeclineInCommittee,
    canApproveInCommittee,
    canViewInstrumentation,
    canEditInstrumentation,
    currentRole,
  };
};

export default useRole;
